/**
 * Etapas del proyector, en el orden en que el backend las ejecuta, con lo que
 * suele tardar cada una.
 *
 * El orden sigue a `docvault.renewals.projection.project_renewal_folder`: primero
 * `_assert_renewable`, después `_create_renewal_folder` —que es donde corren las
 * dos ruletas y se asigna el folio—, luego `_materialize_subfolders` y al final
 * `_project_requirements`, que es el paso largo porque copia cada archivo en el
 * almacenamiento. Narrarlo al revés sería mentir sobre lo que está pasando justo
 * cuando la espera se hace larga.
 *
 * Son una ESTIMACIÓN, no telemetría: la renovación es un único POST que no
 * abre canal de progreso, así que el único dato real que tenemos durante la
 * espera es el tiempo transcurrido. Se narran igualmente porque el caso que hay
 * que evitar es el peor de todos: un spinner mudo durante medio minuto hace que
 * el CX vuelva a pulsar, y el segundo clic es justo el que el backend rechaza
 * con "este expediente ya se renovó".
 */
export const RENEWAL_STAGES: ReadonlyArray<{ label: string; seconds: number }> =
  [
    { label: "Comprobando que el expediente se pueda renovar", seconds: 2 },
    {
      label: "Creando el folio y asignando abogado y mesa de control",
      seconds: 10,
    },
    { label: "Proyectando participantes y datos del expediente", seconds: 8 },
    { label: "Copiando los documentos ya entregados", seconds: 25 },
  ];

/** Lo que se narra cuando la espera ya pasó de lo estimado. */
export const RENEWAL_OVERTIME_LABEL = "Terminando de crear el expediente";

const RENEWAL_TOTAL_SECONDS = RENEWAL_STAGES.reduce(
  (total, stage) => total + stage.seconds,
  0,
);

/**
 * La barra se detiene antes del final mientras no haya respuesta: decir 100%
 * con la petición en vuelo sería la única forma de mentir de esta pantalla.
 */
export const RENEWAL_PROGRESS_CEILING = 95;

/** La etapa que se narra tras `elapsedSeconds` de espera. */
export const renewalStageFor = (elapsedSeconds: number): string => {
  let threshold = 0;
  for (const stage of RENEWAL_STAGES) {
    threshold += stage.seconds;
    if (elapsedSeconds < threshold) return stage.label;
  }
  // Si se pasa de lo estimado seguimos esperando: la transacción del servidor
  // no se cancela porque el cliente se aburra.
  return RENEWAL_OVERTIME_LABEL;
};

/**
 * El porcentaje de la barra tras `elapsedSeconds` de espera, con la petición
 * todavía en vuelo. Nunca pasa del techo: el 100 lo pone sólo la respuesta.
 */
export const renewalProgressPercent = (elapsedSeconds: number): number =>
  Math.min(
    RENEWAL_PROGRESS_CEILING,
    Math.round(
      (elapsedSeconds / RENEWAL_TOTAL_SECONDS) * RENEWAL_PROGRESS_CEILING,
    ),
  );
